import { animationRise } from "asset/framer-motion-util";
import ButtonBox from "component/ButtonBox/ButtonBox";
import InputBox from "component/InputBox/InputBox";
import InputError from "component/InputError/InputError";
import LinkButton from "component/LinkButton/LinkButton";
import Loader from "component/Loader/Loader";
import Text from "component/Text";
import ToastCustom from "component/Toast";
import ForgotPasswordController from "controller/ForgotPassword.controller";
import { motion } from "framer-motion";
import { withRouter } from "hoc/withRouter";
import useDarkMode from "hook/useDarkMode";
import queryString from "query-string";
import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import forgotPasswordReducer, {
  INITIAL_FORGOT_PASSWORD_STATE,
} from "reducer/auth/ForgotPasswordReducer";
import { linkPageLogin } from "routes";
import SimpleReactValidator from "simple-react-validator";
import {
  attachRadialGradientListener,
  getStandardValidator,
  isAnEmail,
  messageReplace,
  parseStandardAPIErrorMessage,
} from "util/CommonUtil";
import { TOAST_VARIANT } from "util/Constant";

import { CheckIcon } from "@heroicons/react/16/solid";
import DarkAndLightModeSwitch from "component/DarkAndLightModeSwitch/DarkAndLightModeSwitch";
import { AuthPageWelcomeHeader } from "component/sections/AuthPageWelcomeHeader/AuthPageWelcomeHeader";
import useIsWhitelabelled from "hook/useIsWhitelabelled";

const PageForgotPassword = () => {
  const toastRef = useRef<any>("");
  const [loader, setLoader] = useState(true);

  const location = useLocation();

  const { productName } = useIsWhitelabelled();
  const isDarkMode = useDarkMode();

  useEffect(() => {
    const container = document.getElementById("container");
    const cleanup = attachRadialGradientListener(container, isDarkMode);
    setLoader(false);
    return cleanup;
  }, [isDarkMode]);

  const [submitButtonLoader, setSubmitButtonLoader] = useState<boolean>(false); // Initialize login button loader state
  const [forgotPasswordState, dispatch] = useReducer(forgotPasswordReducer, {
    ...INITIAL_FORGOT_PASSWORD_STATE,
  });

  const { email, isResetLinkSent, toastMessage } = forgotPasswordState.data;

  // Create an instance of LoginController to manage form actions
  const forgotPasswordController = new ForgotPasswordController(forgotPasswordState, dispatch);

  const simpleValidator = useRef(
    new SimpleReactValidator({
      validators: { ...getStandardValidator() },
      ...messageReplace(),
      autoForceUpdate: {
        forceUpdate: () => forgotPasswordController.forceUpdate(!forgotPasswordState.forceUpdate),
      },
    })
  );

  const query = queryString.parse(location.search, { decode: false });

  useEffect(() => {
    if (isAnEmail(query?.email as string)) {
      forgotPasswordController.handleInputChange({
        target: { name: "email", value: query?.email },
      } as React.ChangeEvent<HTMLInputElement>);
    }
  }, []);

  useEffect(() => {
    if (isResetLinkSent) {
      setTimeout(() => {
        forgotPasswordController.revertIsResetLinkSent();
      }, 10000);
    }
  }, [isResetLinkSent]);

  useEffect(() => {
    if (toastMessage !== "") {
      toastRef.current.showFunction();
    }
  }, [toastMessage]);

  /* 
      -----------------------------------------------------------------------------------------------------------------------
      Validators:
      -----------------------------------------------------------------------------------------------------------------------
    */

  const validate = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    simpleValidator?.current?.showMessageFor(e?.target?.name);
  }, []);

  // Function to validate the form
  const validateForm = useCallback(() => {
    let isValid = false; // Initialize isValid flag
    isValid = simpleValidator?.current?.allValid(); // Check if all fields are valid
    if (isValid) {
      // If form is valid
      return true; // Return true
    } else {
      // If form is not valid
      forgotPasswordController.forceUpdate(!forgotPasswordState.forceUpdate); // Force update to show errors
      simpleValidator?.current?.showMessages(); // Show error messages
      forgotPasswordController.forceUpdate(!forgotPasswordState.forceUpdate); // Force update again to ensure errors are displayed
      return false; // Return false
    }
  }, []);

  // Event handlers for form inputs
  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      forgotPasswordController.handleInputChange(event);
    },
    [forgotPasswordController]
  );

  // Handle form submission
  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event?.preventDefault(); // Prevent default form submission behavior
      setSubmitButtonLoader(true); // Set login button loader to true
      if (!validateForm()) {
        setSubmitButtonLoader(false); // Set login button loader to false
        return Promise.reject(new Error("Form validation failed")); // Reject promise if form validation fails
      }

      try {
        await forgotPasswordController.sendResetLink(); // Send reset link
        forgotPasswordController.setErrorMessage("");
        return Promise.resolve(); // Resolve promise
      } catch (error: any) {
        const errorMessage =
          parseStandardAPIErrorMessage(error) ?? error?.message ?? "An error occurred"; // Parse and set error message
        forgotPasswordController.setErrorMessage(errorMessage); // Set error message in case of an error
        return Promise.reject(new Error("Error")); // Reject promise with an error message
      } finally {
        setSubmitButtonLoader(false); // Set login button loader to false
      }
    },
    [email]
  ); // Dependencies for the handleSubmit function

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        handleSubmit(new Event("submit") as unknown as React.FormEvent);
      }
    };

    window.addEventListener("keypress", handleKeyPress);
    return () => {
      window.removeEventListener("keypress", handleKeyPress);
    };
  }, [handleSubmit]);

  const renderPrimaryButton = () => {
    if (!isResetLinkSent) {
      return (
        <>
          <ButtonBox
            label="label.text.forgotPassword.button.resetLink"
            color="gradient-blue"
            onClickWithLoader={handleSubmit}
            disabled={isResetLinkSent ?? false}
            showButtonLoader={submitButtonLoader}
          />
          <InputError
            validation={forgotPasswordState.errorMessage}
            errorClassNames="mt-1 flex justify-center mb-2 pl-4.5"
          />
        </>
      );
    } else {
      return (
        <div className="pb-5">
          <ButtonBox
            className="pointer-events-none"
            isIcon
            iconImg={<CheckIcon className="h-5 w-5" />}
            label={"label.button.inviteUserDialog.linkSent"}
            onClick={() => {
              console.log(" >> Link sent successfully");
            }}
            // disabled
          />
        </div>
      );
    }
  };

  return (
    <div
      id="container"
      className="lg:min-w-screen flex min-h-screen w-full items-center justify-center bg-light-grey-100 dark:bg-dark-patch-radial-gradient dark:text-dark-text-main"
    >
      {loader ? (
        <Loader />
      ) : (
        <div className="lg:w-266 m-auto">
          <div className="fixed right-6 top-6 z-10 max-w-fit">
            <DarkAndLightModeSwitch />
          </div>

          <div className="flex flex-col items-center gap-6 p-6 xs:gap-x-16 xs:gap-y-6 xs:p-6 lg:flex-row">
            <AuthPageWelcomeHeader>
              <div className="text-body-copy-2 text-light-grey-600 dark:text-dark-text-v2 lg:w-111">
                <Text label={"label.text.login.welcomeNote.bottom.line1"} args={{ productName }} />
                <br />
                <Text label={"label.text.login.welcomeNote.bottom.line2"} />
                <br />
                <Text label={"label.text.login.welcomeNote.bottom.line3"} />
              </div>
            </AuthPageWelcomeHeader>

            <motion.div
              {...animationRise}
              className="w-full rounded-xl bg-light-grey-50 p-6 shadow-light-black-transparent-25 backdrop-blur-xs dark:bg-dark-login-card dark:shadow-dark-black-transparent-25 xs:px-10 xs:py-12 sm:w-3/5 lg:w-129 lg:px-16"
            >
              <h2 className="mb-4 text-2xl font-light leading-none">
                <Text label="label.text.forgotPassword.title" className="font-light" />
              </h2>

              <div className="mb-4">
                <Text
                  className="text-body-copy-2 font-normal text-light-grey-600 dark:text-dark-text-main"
                  label={"label.text.forgotPassword.description"}
                />
              </div>

              <div className="mb-3 flex flex-col gap-y-3">
                <InputBox
                  label="label.text.login.input.email"
                  placeholder="Enter email"
                  name="email"
                  onChange={handleInputChange}
                  value={email}
                  onBlur={validate}
                  validation={simpleValidator?.current?.message(
                    "email",
                    email,
                    "mandatory|email|noSpace"
                  )}
                />
              </div>

              {renderPrimaryButton()}

              <div className="flex justify-center">
                <LinkButton
                  className="max-w-fit pl-4.5"
                  path={
                    linkPageLogin +
                    (isAnEmail(query.email as string) ? `?email=${query.email}` : "")
                  }
                  label={"label.text.forgotPassword.button.backToLogin"}
                />
              </div>
            </motion.div>
          </div>
        </div>
      )}

      <ToastCustom
        position="top-right"
        ref={toastRef}
        toastLife={5000}
        message={toastMessage ?? ""}
        variantType={TOAST_VARIANT.PRIMARY}
      />
    </div>
  );
};

export default withRouter(PageForgotPassword);
